import { TableRow, TableCell, Button, Chip } from '@heroui/react';
import React from 'react';
import { FiEye, FiShoppingCart } from 'react-icons/fi';

import {
  LoadingTable,
  LoadingTableColumn,
} from '../../../../../components/Table';
import type { OrderWithCustomProducts } from '../../../data/utils/types';

interface OrderTableProps {
  orders: OrderWithCustomProducts[];
  isLoading?: boolean;
  onOrderClick?: (orderId: string) => void;
}

const getStatusColor = (
  status?: string | null
): 'default' | 'primary' | 'success' | 'warning' | 'danger' => {
  if (!status) return 'default';
  const upperStatus = status.toUpperCase();
  if (upperStatus === 'PENDING') return 'warning';
  if (upperStatus === 'CONFIRMED' || upperStatus === 'PROCESSING')
    return 'primary';
  if (upperStatus === 'SHIPPED' || upperStatus === 'DELIVERED')
    return 'success';
  if (upperStatus === 'CANCELLED' || upperStatus === 'REFUNDED')
    return 'danger';
  return 'default';
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  } catch (error) {
    return 'N/A';
  }
};

export const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  isLoading = false,
  onOrderClick,
}) => {
  const handleViewOrder = (orderId: string) => {
    onOrderClick?.(orderId);
  };

  const columns: LoadingTableColumn[] = [
    { label: 'Order Number', className: 'pl-6' },
    { label: 'Buyer', className: 'hidden md:table-cell' },
    { label: 'Distributor', className: 'hidden lg:table-cell' },
    { label: 'Status', className: 'hidden md:table-cell' },
    { label: 'Total', className: 'hidden lg:table-cell' },
    { label: 'Date', className: 'hidden lg:table-cell' },
    { label: 'Actions', width: 80, className: 'pr-6' },
  ];

  const renderRow = (order: OrderWithCustomProducts) => {
    // Get buyer and supplier names
    const buyerName = order.buyer?.displayName || order.buyer?.name || 'N/A';
    const supplierName = order.supplier?.name || 'N/A';
    const orderNumber = order.referenceId || order.id;

    return (
      <TableRow key={order.id}>
        <TableCell className="pl-6">
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{orderNumber}</span>
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <span className="text-sm text-foreground">{buyerName}</span>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="text-sm text-foreground">{supplierName}</span>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <div className="flex flex-col gap-1">
            {order.status && (
              <Chip
                size="sm"
                variant="flat"
                color={getStatusColor(order.status)}
              >
                {order.status}
              </Chip>
            )}
            {order.orderSupplierStatus && (
              <Chip
                size="sm"
                variant="flat"
                color={getStatusColor(order.orderSupplierStatus)}
              >
                {order.orderSupplierStatus}
              </Chip>
            )}
            {!order.status && !order.orderSupplierStatus && (
              <span className="text-sm text-default-500">N/A</span>
            )}
          </div>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="font-medium text-foreground">
            {order.products?.length > 0
              ? `${order.products.length} item${
                  order.products.length > 1 ? 's' : ''
                }`
              : 'N/A'}
          </span>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="text-sm text-foreground">
            {formatDate(order.createdAt)}
          </span>
        </TableCell>
        <TableCell className="pr-6">
          <div className="flex gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              color="primary"
              onPress={() => handleViewOrder(order.id)}
              aria-label="View order"
            >
              <FiEye className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <LoadingTable
      columns={columns}
      data={orders}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={orders.length === 0}
      rowCount={5}
      ariaLabel="Orders table"
      emptyState={{
        title: 'No orders found',
        description: 'There are no orders available.',
        icon: FiShoppingCart,
        iconBgClass: 'bg-primary-50',
        iconColorClass: 'text-primary-500',
      }}
    />
  );
};
